import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { PALETTE } from '../palette.js';
import { Button, Pill } from '../components/primitives.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import {
  IconCheck,
  IconExternal,
  IconInfo,
  IconKey,
  IconSettings,
  IconUsers,
} from '../components/icons.jsx';

const ADMIN_ROLES = new Set(['owner', 'admin']);
const UNSAFE_PROVIDER_TEXT_PATTERN =
  /(^|[_-])(token|signature|sig|password|secret|key|api[_-]?key|access[_-]?token|refresh[_-]?token)([_-]|$)/i;
const KNOWN_ACCOUNT_SIGN_IN_PROVIDERS = ['google', 'apple', 'facebook'];
const ACCOUNT_SIGN_IN_PROVIDER_KEYS = new Set(KNOWN_ACCOUNT_SIGN_IN_PROVIDERS);
const ACCOUNT_SIGN_IN_PROVIDER_REGISTRY = {
  google: {
    label: 'Google',
    scope: 'Account sign-in',
    connectedBody: 'Google can authenticate this personal account.',
    unavailableBody: 'Google sign-in is not configured for this environment yet.',
    connectedAction: 'Unlink disabled',
    unavailableAction: 'Setup pending',
  },
  apple: {
    label: 'Apple',
    scope: 'Account sign-in',
    connectedBody: 'Apple can authenticate this personal account.',
    unavailableBody: 'Apple sign-in is not configured for this environment yet.',
    connectedAction: 'Unlink disabled',
    unavailableAction: 'Setup pending',
  },
  facebook: {
    label: 'Facebook',
    scope: 'Account sign-in',
    connectedBody: 'Facebook can authenticate this personal account.',
    unavailableBody: 'Facebook sign-in is not configured for this environment yet.',
    connectedAction: 'Unlink disabled',
    unavailableAction: 'Setup pending',
  },
};

export function titleCase(value) {
  return String(value || '')
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Unknown';
}

export function initials(value) {
  return String(value || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase() || '?';
}

export function authProviderLabel(user = {}) {
  const auth = user.auth || {};
  const provider = auth.provider || auth.source || user.provider || '';
  if (provider) return `${titleCase(auth.type || 'auth')} via ${titleCase(provider)}`;
  return titleCase(auth.type || 'legacy');
}

function safeProviderKey(value) {
  const provider = String(value || '').trim().toLowerCase();
  if (!provider || !/^[a-z0-9_-]{1,40}$/.test(provider)) return null;
  return provider;
}

function safeProviderTimestamp(value) {
  if (typeof value !== 'string') return '';
  const timestamp = value.trim();
  if (!timestamp || timestamp.length > 40 || UNSAFE_PROVIDER_TEXT_PATTERN.test(timestamp)) return '';
  if (!/^\d{4}-\d{2}-\d{2}(?:T|\s)\d{2}:\d{2}(?::\d{2})?(?:\.\d{1,6})?(?:Z|[+-]\d{2}:?\d{2})?$/.test(timestamp)) {
    return '';
  }
  return timestamp;
}

function normalizeProviderCapabilities(auth = {}) {
  const capabilities = auth.providerCapabilities || auth.signInProviderCapabilities || {};
  if (!capabilities || typeof capabilities !== 'object' || Array.isArray(capabilities)) return {};

  return Object.entries(capabilities).reduce((normalized, [rawKey, rawConfig]) => {
    const key = safeProviderKey(rawKey);
    if (!key || !rawConfig || typeof rawConfig !== 'object' || Array.isArray(rawConfig)) return normalized;
    normalized[key] = {
      configured: rawConfig.configured === true,
      linkAvailable: rawConfig.linkAvailable === true || rawConfig.connectAvailable === true,
      reauthRequired: rawConfig.reauthRequired === true,
      lastUpdated: safeProviderTimestamp(rawConfig.lastUpdated) || safeProviderTimestamp(rawConfig.lastVerifiedAt),
    };
    return normalized;
  }, {});
}

function uniqueProviders(values = []) {
  return Array.from(new Set(
    values
      .map(safeProviderKey)
      .filter((provider) => provider && ACCOUNT_SIGN_IN_PROVIDER_KEYS.has(provider)),
  ));
}

function providerRowFor(key, { connected, capability } = {}) {
  const config = ACCOUNT_SIGN_IN_PROVIDER_REGISTRY[key] || {
    label: titleCase(key),
    scope: 'Account sign-in',
    connectedBody: `${titleCase(key)} can authenticate this personal account.`,
    unavailableBody: `${titleCase(key)} sign-in is not configured for this environment yet.`,
    connectedAction: 'Unlink disabled',
    unavailableAction: 'Setup pending',
  };

  if (connected && capability?.reauthRequired) {
    return {
      key,
      label: config.label,
      scope: config.scope,
      body: `${config.connectedBody} Reauthentication is required before it should be treated as healthy.`,
      status: 'Reauth required',
      tone: 'warn',
      action: 'Reauth gated',
      lastUpdated: capability.lastUpdated || '',
    };
  }

  if (connected) {
    return {
      key,
      label: config.label,
      scope: config.scope,
      body: config.connectedBody,
      status: 'Connected',
      tone: 'live',
      action: config.connectedAction,
      lastUpdated: capability?.lastUpdated || '',
    };
  }

  if (capability?.configured && capability?.linkAvailable) {
    return {
      key,
      label: config.label,
      scope: config.scope,
      body: `${config.label} provider configuration is present. The connect action remains gated until a safe account-link route is enabled.`,
      status: 'Connect available',
      tone: 'accent',
      action: 'Connect gated',
      lastUpdated: capability.lastUpdated || '',
    };
  }

  return {
    key,
    label: config.label,
    scope: config.scope,
    body: config.unavailableBody,
    status: 'Not configured',
    tone: 'neutral',
    action: config.unavailableAction,
    lastUpdated: capability?.lastUpdated || '',
  };
}

export function connectedSignInRows(user = {}) {
  const auth = user.auth || {};
  const connectedProviders = uniqueProviders([
    auth.provider,
    auth.source,
    user.provider,
    ...(Array.isArray(auth.providers) ? auth.providers : []),
  ]);
  const connectedSet = new Set(connectedProviders);
  const capabilities = normalizeProviderCapabilities(auth);
  const providerKeys = uniqueProviders([
    ...KNOWN_ACCOUNT_SIGN_IN_PROVIDERS,
    ...Object.keys(capabilities),
    ...connectedProviders,
  ]);

  return providerKeys.map((key) => providerRowFor(key, {
    connected: connectedSet.has(key),
    capability: capabilities[key],
  }));
}

export function normalizeMemberships(user = {}) {
  const memberships = Array.isArray(user.memberships) ? user.memberships : [];
  return memberships.map((membership) => ({
    ...membership,
    companyId: membership.companyId || membership.company?.id || '',
    company: membership.company || null,
    role: membership.role || 'viewer',
    status: membership.status || 'active',
  }));
}

export function canOpenCompanySettings(membership) {
  return ADMIN_ROLES.has(membership?.role);
}

export function passwordChangeCapability(user = {}) {
  if (user.auth?.type !== 'supabase') {
    return {
      available: false,
      status: 'Unavailable',
      tone: 'neutral',
      body: 'Password change is unavailable for this legacy account context.',
    };
  }

  if (user.auth?.passwordChange?.available === true) {
    return {
      available: true,
      status: 'Available',
      tone: 'live',
      body: 'Use your current password to set a new password.',
    };
  }

  if (user.auth?.passwordChange?.reason === 'external_provider') {
    return {
      available: false,
      status: 'External sign-in',
      tone: 'neutral',
      body: 'Password is managed by the connected sign-in provider.',
    };
  }

  return {
    available: false,
    status: 'Checking',
    tone: 'accent',
    body: 'Password change becomes available after live account capabilities load.',
  };
}

function activeCompanyIdFor(user = {}) {
  user = user || {};
  return user.activeCompany?.id || user.tenant?.companyId || normalizeMemberships(user)[0]?.companyId || '';
}

export function accountUserFromProfileResponse(response = {}, fallbackUser = {}) {
  if (response?.auth?.type !== 'supabase') return fallbackUser;
  const responseUser = response.user || {};
  return {
    ...fallbackUser,
    ...responseUser,
    auth: response.auth,
    activeCompany: response.activeCompany || responseUser.activeCompany || null,
    role: response.role || responseUser.role || null,
    memberships: response.memberships || responseUser.memberships || [],
    tenant: response.tenant || responseUser.tenant || null,
    platform: response.platform || responseUser.platform || fallbackUser.platform || { admin: false },
  };
}

export function passwordChangeErrorMessage(err = {}) {
  if (err.code === 'current_password_invalid') return 'Current password could not be verified.';
  if (err.code === 'new_password_weak' || err.code === 'new_password_reused') {
    return 'Use a stronger new password that is different from the current password.';
  }
  if (err.code === 'password_change_unavailable') {
    return 'Password change is unavailable for this sign-in method.';
  }
  if (err.status === 401) return 'Sign in again before changing your password.';
  return 'Password could not be changed safely.';
}

export function emailChangeRequestBlocker({ nextEmail, currentEmail, pendingEmail } = {}) {
  const requestedEmail = String(nextEmail || '').trim().toLowerCase();
  const activeEmail = String(currentEmail || '').trim().toLowerCase();
  const alreadyPendingEmail = String(pendingEmail || '').trim().toLowerCase();

  if (!requestedEmail || !requestedEmail.includes('@')) {
    return { status: 'error', message: 'Enter a valid email address.' };
  }
  if (requestedEmail === activeEmail) {
    return { status: 'idle', message: 'Email is already current.' };
  }
  if (alreadyPendingEmail && requestedEmail === alreadyPendingEmail) {
    return { status: 'idle', message: 'Verification is already pending for this address.' };
  }
  return null;
}

export default function AccountCenter({ session = {}, navigate = () => {} }) {
  const [profileState, setProfileState] = useState({ status: 'idle', user: null, error: null });
  const [identityForm, setIdentityForm] = useState({ displayName: '', email: '' });
  const [identityBaseline, setIdentityBaseline] = useState({ userId: '', displayName: '', email: '' });
  const [profileSaveState, setProfileSaveState] = useState({ status: 'idle', message: '' });
  const [emailChangeState, setEmailChangeState] = useState({ status: 'idle', message: '', pendingEmail: '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordState, setPasswordState] = useState({ status: 'idle', message: '' });
  const sessionUserId = session.user?.id || '';
  const sessionActiveCompanyId = activeCompanyIdFor(session.user);

  useEffect(() => {
    if (session.status !== 'ready' || !session.user || session.user.auth?.type !== 'supabase') {
      setProfileState({ status: 'idle', user: null, error: null });
      return undefined;
    }

    let alive = true;
    setProfileState((current) => ({ ...current, status: 'loading', error: null }));
    const options = sessionActiveCompanyId
      ? { headers: { 'x-company-id': sessionActiveCompanyId } }
      : undefined;

    api.get('/api/saas/profile', options)
      .then((response) => {
        if (!alive) return;
        setProfileState({
          status: 'ready',
          user: accountUserFromProfileResponse(response, session.user),
          error: null,
        });
      })
      .catch((err) => {
        if (!alive) return;
        setProfileState({ status: 'error', user: null, error: err });
      });

    return () => {
      alive = false;
    };
  }, [session.status, sessionUserId, sessionActiveCompanyId]);

  const effectiveUser = profileState.user || session.user || null;
  const effectiveUserKey = [
    effectiveUser?.id || '',
    effectiveUser?.displayName || '',
    effectiveUser?.name || '',
    effectiveUser?.email || '',
  ].join('|');

  useEffect(() => {
    if (!effectiveUser) {
      setIdentityForm({ displayName: '', email: '' });
      setIdentityBaseline({ userId: '', displayName: '', email: '' });
      setProfileSaveState({ status: 'idle', message: '' });
      setEmailChangeState({ status: 'idle', message: '', pendingEmail: '' });
      return;
    }

    const next = {
      userId: effectiveUser.id || '',
      displayName: effectiveUser.displayName || effectiveUser.name || '',
      email: effectiveUser.email || '',
    };
    setIdentityForm({ displayName: next.displayName, email: next.email });
    setIdentityBaseline(next);
  }, [effectiveUserKey]);

  if (session.status === 'loading') {
    return (
      <PageShell>
        <ProtectedPageLoader />
      </PageShell>
    );
  }

  if (session.status === 'error') {
    return (
      <PageShell>
        <StaticHeader eyebrow="Account" title="Account unavailable" />
        <EmptyState
          tone="warn"
          title="Profile could not be loaded"
          body="Refresh and sign in again if the account context does not return."
        />
      </PageShell>
    );
  }

  const user = effectiveUser;
  if (!user) {
    return (
      <PageShell>
        <StaticHeader eyebrow="Account" title="Sign in required" />
        <EmptyState
          title="No account session"
          body="Sign in to view your profile, company roles, security settings, and connected sign-in state."
        />
      </PageShell>
    );
  }

  const memberships = normalizeMemberships(user);
  const displayName = user.displayName || user.name || user.email?.split('@')[0] || 'Account';
  const email = user.email || 'Email unavailable';
  const identityFormReady = identityBaseline.userId === (user.id || '');
  const displayNameInputValue = identityFormReady ? identityForm.displayName : (user.displayName || user.name || '');
  const emailInputValue = identityFormReady ? identityForm.email : (user.email || '');
  const isPlatformAdmin = Boolean(user.platform?.admin);
  const profileBusy = profileSaveState.status === 'saving';
  const emailBusy = emailChangeState.status === 'saving';
  const passwordCapability = passwordChangeCapability(user);
  const passwordBusy = passwordState.status === 'saving';
  const connectedSignIns = connectedSignInRows(user);

  function updateIdentityField(field, value) {
    setIdentityForm((current) => ({ ...current, [field]: value }));
    if (field === 'displayName' && profileSaveState.status === 'error') {
      setProfileSaveState({ status: 'idle', message: '' });
    }
    if (field === 'email' && emailChangeState.status === 'error') {
      setEmailChangeState({ status: 'idle', message: '', pendingEmail: '' });
    }
  }

  async function submitDisplayName(event) {
    event.preventDefault();
    if (profileBusy) return;
    const nextDisplayName = displayNameInputValue.trim();
    if (!nextDisplayName) {
      setProfileSaveState({ status: 'error', message: 'Display name is required.' });
      return;
    }
    if (nextDisplayName === identityBaseline.displayName) {
      setProfileSaveState({ status: 'idle', message: 'Display name is already current.' });
      return;
    }

    setProfileSaveState({ status: 'saving', message: 'Saving profile...' });
    try {
      const response = await api.patch('/api/saas/profile', { displayName: nextDisplayName });
      const nextUser = accountUserFromProfileResponse(response, user);
      const confirmedDisplayName = nextUser.displayName || nextUser.name || nextDisplayName;
      setProfileState({ status: 'ready', user: nextUser, error: null });
      setIdentityBaseline({
        userId: nextUser.id || '',
        displayName: confirmedDisplayName,
        email: nextUser.email || '',
      });
      setIdentityForm((current) => ({ ...current, displayName: confirmedDisplayName }));
      setProfileSaveState({ status: 'success', message: 'Display name saved.' });
    } catch {
      setProfileSaveState({ status: 'error', message: 'Display name could not be saved safely.' });
    }
  }

  async function submitEmailChange(event) {
    event.preventDefault();
    if (emailBusy) return;
    const nextEmail = emailInputValue.trim().toLowerCase();
    const blocker = emailChangeRequestBlocker({
      nextEmail,
      currentEmail: identityBaseline.email,
      pendingEmail: emailChangeState.pendingEmail,
    });
    if (blocker) {
      setEmailChangeState((current) => ({
        status: blocker.status,
        message: blocker.message,
        pendingEmail: current.pendingEmail,
      }));
      return;
    }

    setEmailChangeState({ status: 'saving', message: 'Requesting verification...', pendingEmail: '' });
    try {
      const response = await api.post('/api/saas/profile/email-change', { email: nextEmail });
      setEmailChangeState({
        status: 'success',
        message: 'Verification email sent. The account email changes after the new address is confirmed.',
        pendingEmail: response.pendingEmail || nextEmail,
      });
    } catch (err) {
      const message = err?.code === 'session_reauth_required'
        ? 'Sign in again before changing your email.'
        : 'Email change could not be requested safely.';
      setEmailChangeState({ status: 'error', message, pendingEmail: '' });
    }
  }

  function updatePasswordField(field, value) {
    setPasswordForm((current) => ({ ...current, [field]: value }));
    if (passwordState.status === 'error') setPasswordState({ status: 'idle', message: '' });
  }

  async function submitPasswordChange(event) {
    event.preventDefault();
    if (!passwordCapability.available || passwordBusy) return;
    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      setPasswordState({ status: 'error', message: 'Enter your current password and a new password.' });
      return;
    }
    if (passwordForm.newPassword.length < 12) {
      setPasswordState({ status: 'error', message: 'Use at least 12 characters for the new password.' });
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordState({ status: 'error', message: 'New password confirmation does not match.' });
      return;
    }

    setPasswordState({ status: 'saving', message: 'Changing password...' });
    try {
      await api.post('/api/saas/profile/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordState({ status: 'success', message: 'Password changed.' });
    } catch (err) {
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordState({ status: 'error', message: passwordChangeErrorMessage(err) });
    }
  }

  return (
    <PageShell>
      <header style={heroStyle}>
        <div style={avatarStyle}>{initials(displayName || email)}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={eyebrowStyle}>Account Center</div>
          <h1 style={titleStyle}>{displayName}</h1>
          <div style={emailStyle}>{email}</div>
        </div>
        {isPlatformAdmin && (
          <div style={heroPillsStyle}>
            <Pill tone="accent">Platform Operator</Pill>
          </div>
        )}
      </header>

      {profileState.status === 'error' && (
        <div style={{ marginBottom: 18 }}>
          <EmptyState
            compact
            tone="warn"
            title="Live profile API unavailable"
            body="Showing the current signed-in session context without exposing account tokens or provider credentials."
          />
        </div>
      )}

      <div style={sectionStackStyle}>
          <Section
            icon={<IconUsers size={16} />}
            title="Profile"
            subtitle="Personal identity used by the workspace."
          >
            <div style={identityGridStyle}>
              <EditableIdentityField
                label="Display name"
                value={displayNameInputValue}
                autoComplete="name"
                busy={profileBusy}
                buttonLabel="Save profile"
                state={profileSaveState}
                onChange={(value) => updateIdentityField('displayName', value)}
                onSubmit={submitDisplayName}
              />
              <EditableIdentityField
                label="Email"
                type="email"
                value={emailInputValue}
                autoComplete="email"
                busy={emailBusy}
                buttonLabel="Request email change"
                state={emailChangeState}
                help="Changing email requires verification at the new address before it becomes active."
                onChange={(value) => updateIdentityField('email', value)}
                onSubmit={submitEmailChange}
              />
              <InfoField label="Account status" value={user.status ? titleCase(user.status) : 'Active'} />
            </div>
            {emailChangeState.pendingEmail && (
              <div style={pendingEmailStyle}>
                Pending email verification for {emailChangeState.pendingEmail}
              </div>
            )}
          </Section>

          <Section
            icon={<IconUsers size={16} />}
            title="Companies & Roles"
            subtitle="Only companies assigned to this signed-in user are shown."
            aside={<Pill tone={memberships.length ? 'live' : 'warn'}>{memberships.length || 0} assigned</Pill>}
          >
            {memberships.length === 0 ? (
              <EmptyState
                compact
                title="No company memberships"
                body={isPlatformAdmin
                  ? 'This platform operator account is not assigned to a tenant company from the account center.'
                  : 'This account is not assigned to a company yet. Accept an invite or ask a company owner to add you.'}
              />
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {memberships.map((membership) => (
                  <MembershipRow
                    key={membership.id || membership.companyId}
                    membership={membership}
                    onOpenCompany={() => navigate('company')}
                  />
                ))}
              </div>
            )}
          </Section>

          <Section
            icon={<IconSettings size={16} />}
            title="Security"
            subtitle="Account access controls stay separate from tenant settings."
          >
            <div style={{ display: 'grid', gap: 10 }}>
              <StateRow
                icon={<IconCheck size={14} />}
                title="Signed-in session"
                body="Current session is active for this browser."
                status="Active"
                tone="live"
              />
              <StateRow
                icon={<IconKey size={14} />}
                title="Password change"
                body={passwordCapability.body}
                status={passwordCapability.status}
                tone={passwordCapability.tone}
              />
              {passwordCapability.available && (
                <PasswordChangeForm
                  form={passwordForm}
                  state={passwordState}
                  busy={passwordBusy}
                  onFieldChange={updatePasswordField}
                  onSubmit={submitPasswordChange}
                />
              )}
            </div>
          </Section>

          <Section
            icon={<IconExternal size={16} />}
            title="Connected Sign-ins"
            subtitle="Personal sign-in providers, not tenant marketing integrations."
          >
            <div style={connectedSignInsGridStyle}>
              {connectedSignIns.map((provider) => (
                <ConnectedSignInRow key={provider.key} provider={provider} />
              ))}
            </div>
          </Section>

          {isPlatformAdmin && (
            <Section
              icon={<IconInfo size={16} />}
              title="Platform Capability"
              subtitle="Operator context is shown without tenant data access."
            >
              <StateRow
                icon={<IconCheck size={14} />}
                title="Platform operator"
                body={memberships.length
                  ? 'This account also has tenant memberships listed above. Tenant access stays bounded to those memberships.'
                  : 'This account can use Platform Ops, but it has no tenant company membership from this page.'}
                status="Context only"
                tone="accent"
              />
            </Section>
          )}
      </div>
    </PageShell>
  );
}

function MembershipRow({ membership, onOpenCompany }) {
  const companyName = membership.company?.name || membership.companyId || 'Unknown company';
  const slug = membership.company?.slug || membership.companyId || 'not set';
  const canOpen = canOpenCompanySettings(membership);

  return (
    <div style={membershipRowStyle}>
      <div style={miniAvatarStyle}>{initials(companyName)}</div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={rowTitleStyle}>{companyName}</div>
        <div style={rowMetaStyle}>{slug}</div>
      </div>
      <div style={rowPillStackStyle}>
        <Pill tone={canOpen ? 'accent' : 'neutral'} style={smallPillStyle}>{titleCase(membership.role)}</Pill>
        <Pill tone={membership.status === 'active' ? 'live' : 'warn'} style={smallPillStyle}>{titleCase(membership.status)}</Pill>
      </div>
      {canOpen ? (
        <Button variant="ghost" size="sm" onClick={onOpenCompany} style={rowButtonStyle}>
          <IconExternal size={13} /> Open company settings
        </Button>
      ) : (
        <span style={restrictedTextStyle}>No admin controls</span>
      )}
    </div>
  );
}

function EditableIdentityField({
  label,
  type = 'text',
  value,
  autoComplete,
  busy,
  buttonLabel,
  state,
  help,
  onChange,
  onSubmit,
}) {
  return (
    <form style={editableIdentityFieldStyle} onSubmit={onSubmit}>
      <label style={passwordLabelStyle}>
        <span>{label}</span>
        <input
          type={type}
          value={value}
          autoComplete={autoComplete}
          onChange={(event) => onChange(event.target.value)}
          style={passwordInputStyle}
        />
      </label>
      {help && <div style={identityHelpStyle}>{help}</div>}
      <div style={identityActionRowStyle}>
        <Button variant="ghost" size="sm" type="submit" disabled={busy}>
          {busy ? 'Saving...' : buttonLabel}
        </Button>
        {state.message && (
          <span
            role={state.status === 'error' ? 'alert' : 'status'}
            style={{
              ...passwordMessageStyle,
              color: state.status === 'error' ? '#8B2E1A' : PALETTE.ink2,
            }}
          >
            {state.message}
          </span>
        )}
      </div>
    </form>
  );
}

function Section({ icon, title, subtitle, aside, children }) {
  return (
    <section style={sectionStyle}>
      <div style={sectionHeaderStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span style={sectionIconStyle}>{icon}</span>
          <div style={{ minWidth: 0 }}>
            <h2 style={sectionTitleStyle}>{title}</h2>
            <div style={sectionSubtitleStyle}>{subtitle}</div>
          </div>
        </div>
        {aside && <div style={{ flex: '0 0 auto' }}>{aside}</div>}
      </div>
      {children}
    </section>
  );
}

function InfoField({ label, value }) {
  return (
    <div style={infoFieldStyle}>
      <div style={fieldLabelStyle}>{label}</div>
      <div style={fieldValueStyle}>{value || 'Not available'}</div>
    </div>
  );
}

function StateRow({ icon, title, body, status, tone }) {
  return (
    <div style={stateRowStyle}>
      <span style={stateIconStyle}>{icon}</span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={rowTitleStyle}>{title}</div>
        <div style={rowMetaStyle}>{body}</div>
      </div>
      <Pill tone={tone} style={smallPillStyle}>{status}</Pill>
    </div>
  );
}

function ConnectedSignInRow({ provider }) {
  const isConnected = provider.status === 'Connected';
  const icon = isConnected
    ? <IconCheck size={14} />
    : <IconExternal size={14} />;

  return (
    <div style={connectedProviderRowStyle}>
      <span style={stateIconStyle}>{icon}</span>
      <div style={providerMainStyle}>
        <div style={providerTitleLineStyle}>
          <span style={rowTitleStyle}>{provider.label}</span>
          <span style={providerScopeStyle}>{provider.scope}</span>
        </div>
        <div style={rowMetaStyle}>{provider.body}</div>
        {provider.lastUpdated && (
          <div style={providerTimestampStyle}>Last verified {provider.lastUpdated}</div>
        )}
      </div>
      <div style={providerStatusStackStyle}>
        <Pill tone={provider.tone} style={smallPillStyle}>{provider.status}</Pill>
        <span style={providerActionStyle}>{provider.action}</span>
      </div>
    </div>
  );
}

function PasswordChangeForm({ form, state, busy, onFieldChange, onSubmit }) {
  return (
    <form style={passwordFormStyle} onSubmit={onSubmit}>
      <div style={passwordFieldGridStyle}>
        <PasswordField
          label="Current password"
          autoComplete="current-password"
          value={form.currentPassword}
          onChange={(value) => onFieldChange('currentPassword', value)}
        />
        <PasswordField
          label="New password"
          autoComplete="new-password"
          value={form.newPassword}
          onChange={(value) => onFieldChange('newPassword', value)}
        />
        <PasswordField
          label="Confirm new password"
          autoComplete="new-password"
          value={form.confirmPassword}
          onChange={(value) => onFieldChange('confirmPassword', value)}
        />
      </div>
      <div style={passwordActionRowStyle}>
        <Button variant="accent" size="sm" type="submit" disabled={busy}>
          <IconKey size={13} /> {busy ? 'Changing...' : 'Change password'}
        </Button>
        {state.message && (
          <span
            role={state.status === 'error' ? 'alert' : 'status'}
            style={{
              ...passwordMessageStyle,
              color: state.status === 'error' ? '#8B2E1A' : PALETTE.ink2,
            }}
          >
            {state.message}
          </span>
        )}
      </div>
    </form>
  );
}

function PasswordField({ label, value, autoComplete, onChange }) {
  return (
    <label style={passwordLabelStyle}>
      <span>{label}</span>
      <input
        type="password"
        value={value}
        autoComplete={autoComplete}
        onChange={(event) => onChange(event.target.value)}
        style={passwordInputStyle}
      />
    </label>
  );
}

function EmptyState({ title, body, compact = false, tone = 'neutral' }) {
  return (
    <div style={{
      padding: compact ? 16 : 22,
      border: `1px solid ${tone === 'warn' ? '#EBDCB2' : PALETTE.lineSoft}`,
      borderRadius: 10,
      background: tone === 'warn' ? '#FFF8EA' : PALETTE.surfaceAlt,
      color: PALETTE.ink2,
    }}>
      <div style={{ fontSize: 14, color: PALETTE.ink, fontWeight: 700, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}

function PageShell({ children }) {
  return (
    <div style={pageShellStyle}>
      {children}
    </div>
  );
}

function StaticHeader({ eyebrow, title }) {
  return (
    <header style={{ marginBottom: 22 }}>
      <div style={eyebrowStyle}>{eyebrow}</div>
      <h1 style={titleStyle}>{title}</h1>
    </header>
  );
}

const pageShellStyle = {
  padding: 'clamp(18px, 4vw, 40px) clamp(16px, 4vw, 40px) 64px',
  fontFamily: 'var(--active-font-ui, Inter, sans-serif)',
  color: PALETTE.ink,
};

const heroStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '18px 0 24px',
  flexWrap: 'wrap',
};

const avatarStyle = {
  width: 58,
  height: 58,
  borderRadius: 16,
  background: PALETTE.ink,
  color: 'var(--active-accent, #E06A2C)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 18,
  fontWeight: 800,
  flex: '0 0 auto',
};

const heroPillsStyle = {
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
};

const eyebrowStyle = {
  fontSize: 11,
  letterSpacing: 0,
  textTransform: 'uppercase',
  color: PALETTE.ink3,
  fontWeight: 700,
  marginBottom: 6,
};

const titleStyle = {
  fontFamily: 'Instrument Serif, serif',
  fontStyle: 'italic',
  fontSize: 'clamp(34px, 5vw, 48px)',
  fontWeight: 400,
  margin: 0,
  color: PALETTE.ink,
  letterSpacing: 0,
  lineHeight: 1,
};

const emailStyle = {
  marginTop: 7,
  color: PALETTE.ink2,
  fontSize: 14,
  overflowWrap: 'anywhere',
};

const sectionStackStyle = {
  display: 'grid',
  gap: 14,
  minWidth: 0,
};

const sectionStyle = {
  padding: 18,
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 12,
  background: PALETTE.surface,
  boxShadow: '0 1px 2px rgba(26,23,20,0.03)',
};

const sectionHeaderStyle = {
  display: 'flex',
  alignItems: 'start',
  justifyContent: 'space-between',
  gap: 14,
  marginBottom: 16,
  flexWrap: 'wrap',
};

const sectionIconStyle = {
  width: 28,
  height: 28,
  borderRadius: 8,
  background: PALETTE.accentTint,
  color: PALETTE.accentDeep,
  display: 'grid',
  placeItems: 'center',
  flex: '0 0 auto',
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: 16,
  color: PALETTE.ink,
  fontWeight: 800,
  letterSpacing: 0,
};

const sectionSubtitleStyle = {
  marginTop: 4,
  color: PALETTE.ink3,
  fontSize: 13,
  lineHeight: 1.45,
};

const identityGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
  gap: 10,
};

const editableIdentityFieldStyle = {
  display: 'grid',
  gap: 10,
  padding: 13,
  border: `1px solid ${PALETTE.lineSoft}`,
  borderRadius: 10,
  background: PALETTE.surfaceAlt,
  minWidth: 0,
};

const identityActionRowStyle = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 10,
};

const identityHelpStyle = {
  fontSize: 12,
  color: PALETTE.ink3,
  lineHeight: 1.45,
};

const pendingEmailStyle = {
  marginTop: 10,
  padding: '10px 12px',
  border: `1px solid ${PALETTE.lineSoft}`,
  borderRadius: 9,
  background: PALETTE.surfaceAlt,
  color: PALETTE.ink2,
  fontSize: 12.5,
  fontWeight: 700,
  overflowWrap: 'anywhere',
};

const infoFieldStyle = {
  padding: 13,
  border: `1px solid ${PALETTE.lineSoft}`,
  borderRadius: 10,
  background: PALETTE.surfaceAlt,
  minWidth: 0,
};

const fieldLabelStyle = {
  fontSize: 11,
  color: PALETTE.ink3,
  textTransform: 'uppercase',
  letterSpacing: 0,
  fontWeight: 800,
  marginBottom: 7,
};

const fieldValueStyle = {
  fontSize: 14,
  color: PALETTE.ink,
  fontWeight: 700,
  overflowWrap: 'anywhere',
};

const membershipRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 13px',
  border: `1px solid ${PALETTE.lineSoft}`,
  borderRadius: 10,
  background: PALETTE.surface,
  minWidth: 0,
  flexWrap: 'wrap',
};

const miniAvatarStyle = {
  width: 36,
  height: 36,
  borderRadius: 10,
  background: PALETTE.surfaceAlt,
  color: PALETTE.ink,
  display: 'grid',
  placeItems: 'center',
  fontSize: 12,
  fontWeight: 800,
  flex: '0 0 auto',
};

const rowTitleStyle = {
  fontSize: 13.5,
  color: PALETTE.ink,
  fontWeight: 800,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowMetaStyle = {
  marginTop: 3,
  fontSize: 12.5,
  color: PALETTE.ink3,
  lineHeight: 1.4,
  overflowWrap: 'anywhere',
};

const rowPillStackStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flexWrap: 'wrap',
  marginLeft: 'auto',
};

const smallPillStyle = {
  padding: '3px 8px',
  fontSize: 10,
  letterSpacing: 0,
};

const rowButtonStyle = {
  minHeight: 32,
  padding: '7px 10px',
  borderRadius: 8,
  marginLeft: 0,
};

const restrictedTextStyle = {
  fontSize: 12,
  color: PALETTE.ink3,
  fontWeight: 700,
};

const stateRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 13px',
  border: `1px solid ${PALETTE.lineSoft}`,
  borderRadius: 10,
  background: PALETTE.surface,
  minWidth: 0,
  flexWrap: 'wrap',
};

const connectedSignInsGridStyle = {
  display: 'grid',
  gap: 10,
};

const connectedProviderRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 13px',
  border: `1px solid ${PALETTE.lineSoft}`,
  borderRadius: 10,
  background: PALETTE.surface,
  minWidth: 0,
  flexWrap: 'wrap',
};

const stateIconStyle = {
  width: 26,
  height: 26,
  borderRadius: 8,
  background: PALETTE.surfaceAlt,
  color: PALETTE.accentDeep,
  display: 'grid',
  placeItems: 'center',
  flex: '0 0 auto',
};

const providerMainStyle = {
  minWidth: 0,
  flex: '1 1 260px',
};

const providerTitleLineStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  minWidth: 0,
};

const providerScopeStyle = {
  fontSize: 11,
  color: PALETTE.ink3,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 0,
};

const providerTimestampStyle = {
  marginTop: 5,
  fontSize: 11.5,
  color: PALETTE.ink3,
  fontWeight: 700,
};

const providerStatusStackStyle = {
  display: 'grid',
  justifyItems: 'end',
  gap: 6,
  minWidth: 0,
  marginLeft: 'auto',
};

const providerActionStyle = {
  fontSize: 12,
  color: PALETTE.ink3,
  fontWeight: 800,
  textAlign: 'right',
};

const passwordFormStyle = {
  display: 'grid',
  gap: 12,
  padding: 13,
  border: `1px solid ${PALETTE.lineSoft}`,
  borderRadius: 10,
  background: PALETTE.surfaceAlt,
};

const passwordFieldGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
  gap: 10,
};

const passwordLabelStyle = {
  display: 'grid',
  gap: 7,
  minWidth: 0,
  fontSize: 11,
  color: PALETTE.ink3,
  textTransform: 'uppercase',
  letterSpacing: 0,
  fontWeight: 800,
};

const passwordInputStyle = {
  width: '100%',
  minHeight: 38,
  border: `1px solid ${PALETTE.controlBorder}`,
  borderRadius: 9,
  background: PALETTE.surface,
  color: PALETTE.ink,
  font: 'inherit',
  fontSize: 14,
  padding: '9px 11px',
};

const passwordActionRowStyle = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 10,
};

const passwordMessageStyle = {
  fontSize: 12.5,
  lineHeight: 1.4,
  fontWeight: 700,
};
